import { Component } from '@theme/component';
import { clamp, isDesktopBreakpoint, mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { cycleFocus } from '@theme/focus';

/**
 * Category list component.
 *
 * Renders a manually-curated list of category links with a paired image stack.
 *
 *   Desktop: hovering or focusing a link selects it and reveals its image.
 *            The image stack is sticky and vertically centered (CSS handles
 *            the sticky positioning; we only swap which image is active).
 *
 *   Mobile:  the image stack docks to the bottom-right corner. As the user
 *            scrolls the page, the link entering the viewport is auto-selected
 *            (IntersectionObserver) so the image stays in sync with the link
 *            the user is reading.
 *
 * The first link is server-rendered with `aria-current="true"` so the module
 * has a sensible initial state before any JS runs.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement} mediaWrap
 * @property {HTMLAnchorElement[]} [links]
 * @property {HTMLElement[]} [images]
 *
 * @extends {Component<Refs>}
 */
class CategoryList extends Component {
  requiredRefs = ['container', 'mediaWrap'];

  /** @type {IntersectionObserver | null} */
  #scrollObserver = null;

  /** @type {((event: MediaQueryListEvent) => void) | null} */
  #breakpointListener = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);

    this.#breakpointListener = () => this.#syncScrollObserver();
    mediaQueryLarge.addEventListener('change', this.#breakpointListener);
    this.#syncScrollObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#teardownScrollObserver();

    if (this.#breakpointListener) {
      mediaQueryLarge.removeEventListener('change', this.#breakpointListener);
      this.#breakpointListener = null;
    }
  }

  get links() {
    return this.refs.links || [];
  }

  get images() {
    return this.refs.images || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * Select a link by index. Wired to each link's `on:pointerenter` and `on:focus`
   * via the declarative `/select/{index}` attribute in the block markup.
   *
   * @param {number} targetIndex
   */
  select(targetIndex) {
    const { links, images } = this;
    if (!links.length) return;

    const selectedIndex = clamp(targetIndex, 0, links.length - 1);
    if (selectedIndex === this.currentIndex) return;

    for (const [index, link] of links.entries()) {
      if (index === selectedIndex) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }

    for (const [index, image] of images.entries()) {
      if (index === selectedIndex) {
        image.setAttribute('data-active', 'true');
      } else {
        image.removeAttribute('data-active');
      }
    }
  }

  /**
   * Arrow keys cycle focus through the link list — matches the existing
   * collection-links interaction so the two modules feel consistent.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        modifier = 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  };

  /**
   * Mobile-only: drive selection from scroll position via IntersectionObserver.
   * We rebuild the observer whenever the breakpoint changes so it's only active
   * where it should be.
   */
  #syncScrollObserver() {
    this.#teardownScrollObserver();

    if (isDesktopBreakpoint()) return;
    if (prefersReducedMotion()) return;
    if (!this.links.length) return;

    // A band roughly centered on the viewport. A link is considered "entering"
    // when its midpoint crosses into that band, which matches the visual feel
    // of "the link the user is currently looking at".
    this.#scrollObserver = new IntersectionObserver(this.#handleScrollIntersection, {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0,
    });

    for (const link of this.links) {
      this.#scrollObserver.observe(link);
    }
  }

  #teardownScrollObserver() {
    if (!this.#scrollObserver) return;
    this.#scrollObserver.disconnect();
    this.#scrollObserver = null;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleScrollIntersection = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const target = /** @type {HTMLElement} */ (entry.target);
      const indexAttr = target.getAttribute('data-index');
      if (indexAttr === null) continue;
      const index = Number.parseInt(indexAttr, 10);
      if (Number.isNaN(index)) continue;
      this.select(index);
    }
  };
}

if (!customElements.get('category-list-component')) {
  customElements.define('category-list-component', CategoryList);
}
